<template>
  <Section
    :id="id"
    :aria-label="t('carrierPortal.spotMarket.bids', 2)"
    class="spot-market-rate-shop p-0"
  >
    <Card v-if="isInEditMode">
      <template #content>
        <SpotMarketBidEdit
          :spot-market="spotMarket"
          :carrier-setting="carrierSetting"
          :accessorial-codes="accessorialCodes"
          :disabled="disabled"
          @save="onSave"
        />
      </template>
    </Card>

    <ul v-if="spotMarket.data?.bids.length" class="spot-market-bid-list">
      <li
        v-for="(bid, i) in spotMarket.data.bids"
        :key="bid.id"
        class="spot-market-bid-list-item"
      >
        <SpotMarketBidCard
          :bid="bid"
          :show-new-bid-button="i === 0 && !isInEditMode"
          :disabled="disabled"
          @delete="emit('delete', $event)"
          @new-bid="isInEditMode = true"
        />
      </li>
    </ul>
  </Section>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  AccessorialCode,
  CarrierSetting,
  Loadable,
} from '@rygen/rygen-ui/model';

import { RateQuote, SpotMarket } from '@/model';

import SpotMarketBidCard from '@/components/carrierPortal/rateShop/SpotMarketBidCard.vue';
import SpotMarketBidEdit, {
  type BidPayload,
} from '@/components/carrierPortal/rateShop/SpotMarketBidEdit.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Card from 'primevue/card';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  spotMarket: {
    type: Object as PropType<Loadable<SpotMarket>>,
    default: () => ({ loading: false }),
  },
  carrierSetting: {
    type: Object as PropType<Loadable<CarrierSetting>>,
    default: () => ({ loading: false }),
  },
  accessorialCodes: {
    type: Object as PropType<Loadable<AccessorialCode[]>>,
    default: () => ({ loading: false }),
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { id, spotMarket, carrierSetting, accessorialCodes, disabled } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'save', payload: BidPayload): void;
  (event: 'delete', rateQuote: RateQuote): void;
}>();

const { t } = useI18n();

const isInEditMode = ref(false);

const onSave = ({ bid, onSuccessCallback, onErrorCallback }: BidPayload) => {
  const payload = {
    bid,
    onSuccessCallback: () => {
      onSuccessCallback();
      isInEditMode.value = false;
    },
    onErrorCallback: () => {
      onErrorCallback();
      // On error, stay in edit mode
    },
  };
  emit('save', payload);
};

watch(
  () => spotMarket.value.data,
  (newVal) => {
    isInEditMode.value = !newVal?.bids.length;
  },
  { deep: true, immediate: true },
);
</script>

<style>
.spot-market-rate-shop {
  .section-header {
    justify-content: space-between;
  }

  .section-data {
    display: flex;
    flex-flow: column;
    gap: var(--sp-3);

    #spot-market-bid {
      display: flex;
      flex-flow: column;
      gap: var(--sp-3);

      .p-message {
        margin: 0;
      }

      .spot-market-bid-form-row {
        display: flex;
        gap: var(--sp-3);

        &.accessorial-row {
          margin-top: var(--sp-3);

          .carrier-portal-rate-quote-accessorials {
            margin-top: var(--sp-1);
          }
        }
      }
    }
  }

  .spot-market-bid-list {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
    margin: 0;
    padding: 0;
    list-style: none;
  }
}
</style>
